/**
 * Configuration links — one encoding shared by all three bundle configurators.
 *
 * A configured bundle has to survive a round trip through a URL, because the
 * same object is three features at once (Jacob, 2026-09-16): a wishlist entry,
 * a "send this to a parent" share link, and the Shop the Look deep links that
 * already exist. Storing a wish and sharing a wish are then the same code.
 *
 * THE ENCODING is flat, legible query params, namespaced by the component key
 * each configurator already uses internally:
 *
 *     ?sheetType=fitted&sheetSize=180x200cm&sheetColour=snow-white
 *     &duvetStyle=one&duvetSize=230x220cm&duvetColour=sage-green
 *     &pillowSize=60x50cm&pillowColour=sage-green
 *
 * Deliberately NOT a base64 blob. The bedding PDP already ships
 * ?sheetColour=/?duvetColour=/?pillowColour= and those links are live in Shop
 * the Look panels, so this is a superset of a format in production rather than
 * a replacement for it. It also stays readable in a Klaviyo email, an ad URL
 * and a support ticket, which a blob does not.
 *
 * NOTHING HERE IS TRUSTED FOR PRICE OR COMPOSITION. A link carries the
 * shopper's CHOICES only -- colour, size, type -- and each configurator
 * re-derives variants, components and price from its own product data exactly
 * as it does for a human clicking swatches. That matters because a link is
 * user-editable: the Cart Transform's composition guard already rejects a
 * tampered `_components` and falls back to the parent variant price, so the
 * worst a doctored link can do is buy the set at the advertised price.
 *
 * `add=1` opts a link into adding to the cart on arrival. Share links set it;
 * Shop the Look links deliberately do not, since landing a visitor on a
 * browsing page with something already in their cart is a different promise.
 */
(() => {
  if (window.JRConfigLink) return;

  /* Same slugify the configurators' own colour matching uses -- a link says
     "north-sea-blue", the data says "North Sea Blue". */
  const slug = (s) =>
    String(s == null ? '' : s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

  /* Mirrors jr-pdp.js's own norm(): "180x200 cm" -> "180x200cm", and the
     typographic × that creeps in from copy-paste folds onto a plain x. */
  const normSize = (s) =>
    String(s == null ? '' : s).toLowerCase().replace(/\s+/g, '').replace(/×/g, 'x');

  const params = () => {
    try {
      return new URLSearchParams(window.location.search);
    } catch (e) {
      return new URLSearchParams('');
    }
  };

  const API = {
    slug,
    normSize,
    params,

    /* Read one field for one component, e.g. get('sheet', 'Colour'). Returns
       null when absent so callers can tell "not in the link" from "empty". */
    get(key, field) {
      const v = params().get(String(key) + field);
      return v == null || v === '' ? null : v;
    },

    /* Does this link ask to be added to the cart on arrival? */
    wantsAdd() {
      const v = params().get('add');
      return v === '1' || v === 'true';
    },

    /* parts: [{ key, colour, size, type, style }] -- every field optional, so
       a configurator only emits the axes it actually has. Towels have no size,
       the decorative sets have neither size nor type. */
    query(parts, opts) {
      const out = [];
      (parts || []).forEach((p) => {
        if (!p || !p.key) return;
        if (p.type) out.push([p.key + 'Type', slug(p.type)]);
        if (p.style) out.push([p.key + 'Style', slug(p.style)]);
        if (p.size) out.push([p.key + 'Size', normSize(p.size)]);
        if (p.colour) out.push([p.key + 'Colour', slug(p.colour)]);
      });
      if (opts && opts.add) out.push(['add', '1']);
      return out.map(([k, v]) => encodeURIComponent(k) + '=' + encodeURIComponent(v)).join('&');
    },

    /* Absolute, shareable, and pinned to the product being configured rather
       than to whatever path the visitor happens to be on. */
    url(parts, opts) {
      const q = API.query(parts, opts);
      const base = window.location.origin + window.location.pathname;
      return q ? base + '?' + q : base;
    },

    async copy(text) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (e) {
        /* Safari refuses the async clipboard outside a user gesture, and any
           browser refuses it on http. Fall back to the old selection trick
           rather than leaving the button dead. */
        try {
          const ta = document.createElement('textarea');
          ta.value = text;
          ta.setAttribute('readonly', '');
          ta.style.position = 'fixed';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.select();
          const ok = document.execCommand('copy');
          document.body.removeChild(ta);
          return ok;
        } catch (e2) {
          return false;
        }
      }
    }
  };

  window.JRConfigLink = API;
})();
